import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, RefreshCw, Search, UserPlus } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { adminAPI } from '../../services/api/adminAPI';
import { DashboardData, UserRole } from '../../types';
import { CustomSelect } from '../../components/common/CustomSelect';
import { useOutletContext } from 'react-router-dom';

interface User {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	fullName: string;
	role: UserRole;
	status: string;
	clientId?: string;
	client?: {
		name: string;
		type: string;
	};
	lastLoginAt?: string;
	createdAt: string;
}

interface Client {
	id: string;
	name: string;
	type?: string;
	integrations?: {
		zoho?: { enabled: boolean; configured: boolean; lastSync?: string };
		odoo?: { enabled: boolean; configured: boolean; lastSync?: string };
		msd?: { enabled: boolean; configured: boolean; lastSync?: string };
	};
}

interface CreateUserFormData {
	email: string;
	firstName: string;
	lastName: string;
	password: string;
	role: UserRole;
	clientId?: string;
}

interface OutletContext {
	dashboardData: DashboardData | null;
	refreshKey: number;
}

export const UserManagementPage: React.FC = () => {
	const { refreshKey } = useOutletContext<OutletContext>();
	const { isSuperAdmin, isPlatformAdmin, isClientUser, clientId } = usePermissions();
	const [users, setUsers] = useState<User[]>([]);
	const [clients, setClients] = useState<Client[]>([]);
	const [syncActivities, setSyncActivities] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 10;

	// Initialize form with default role based on user's role
	const getDefaultRole = () => {
		if (isClientUser) return UserRole.CLIENT_USER;
		return UserRole.CLIENT_USER; // Default to CLIENT_USER for admins too
	};

	const [formData, setFormData] = useState<CreateUserFormData>({
		email: '',
		firstName: '',
		lastName: '',
		password: '',
		role: getDefaultRole(),
		clientId: isClientUser ? clientId || '' : '',
	});
	const [formErrors, setFormErrors] = useState<Record<string, string>>({});
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (refreshKey > 0) {
			fetchUsers();
			if (!isClientUser) {
				fetchClients();
			}
		}
	}, [refreshKey]);

	// Fetch users on mount
	useEffect(() => {
		fetchUsers();
		if (!isClientUser) {
			fetchClients();
			fetchSyncActivities();
		}
	}, []);

	const fetchUsers = async () => {
		setLoading(true);
		try {
			const response = await adminAPI.listUsers();
			setUsers(response.data || []);
		} catch (error) {
			console.error('Failed to fetch users:', error);
		} finally {
			setLoading(false);
		}
	};

	const fetchClients = async () => {
		try {
			const response = await adminAPI.getClients();
			setClients(response.clients || []);
		} catch (error) {
			console.error('Failed to fetch clients:', error);
		}
	};

	const fetchSyncActivities = async () => {
		try {
			// Fetch recent sync activities to get last sync times
			const response = await adminAPI.getSyncActivities(1, 100);
			setSyncActivities(response.activities || []);
		} catch (error) {
			console.error('Failed to fetch sync activities:', error);
		}
	};

	// Get allowed roles based on current user's role
	const getAllowedRoles = (): UserRole[] => {
		if (isSuperAdmin) {
			return [UserRole.SUPER_ADMIN, UserRole.PLATFORM_ADMIN, UserRole.CLIENT_USER];
		}
		if (isPlatformAdmin) {
			return [UserRole.PLATFORM_ADMIN, UserRole.CLIENT_USER];
		}
		if (isClientUser) {
			return [UserRole.CLIENT_USER];
		}
		return [];
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
		// Clear error for this field
		if (formErrors[name]) {
			setFormErrors((prev) => {
				const newErrors = { ...prev };
				delete newErrors[name];
				return newErrors;
			});
		}
	};

	const validateForm = (): boolean => {
		const errors: Record<string, string> = {};

		if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
			errors.email = 'Valid email is required';
		}
		if (!formData.firstName || formData.firstName.trim().length === 0) {
			errors.firstName = 'First name is required';
		}
		if (!formData.lastName || formData.lastName.trim().length === 0) {
			errors.lastName = 'Last name is required';
		}
		if (!formData.password || formData.password.length < 8) {
			errors.password = 'Password must be at least 8 characters';
		}
		if (formData.role === UserRole.CLIENT_USER && !formData.clientId) {
			errors.clientId = 'Client is required for client users';
		}

		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		setSubmitting(true);
		try {
			const payload = {
				email: formData.email,
				firstName: formData.firstName,
				lastName: formData.lastName,
				password: formData.password,
				role: formData.role,
				...(formData.role === UserRole.CLIENT_USER && formData.clientId ? { clientId: formData.clientId } : {}),
			};

			await adminAPI.createUser(payload);

			// Reset form and close modal
			setFormData({
				email: '',
				firstName: '',
				lastName: '',
				password: '',
				role: getDefaultRole(),
				clientId: isClientUser ? clientId || '' : '',
			});
			setShowCreateModal(false);
			// Refresh users list
			fetchUsers();
		} catch (error: any) {
			console.error('Failed to create user:', error);
			setFormErrors({
				submit: error.response?.data?.error || 'Failed to create user. Please try again.',
			});
		} finally {
			setSubmitting(false);
		}
	};

	const filteredUsers = users.filter(
		(u) =>
			u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
			u.fullName.toLowerCase().includes(searchTerm.toLowerCase()),
	);

	// Calculate pagination
	const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

	// Reset to page 1 when search term changes
	React.useEffect(() => {
		setCurrentPage(1);
	}, [searchTerm]);

	const handlePreviousPage = () => {
		setCurrentPage((prev) => Math.max(prev - 1, 1));
	};

	const handleNextPage = () => {
		setCurrentPage((prev) => Math.min(prev + 1, totalPages));
	};

	const getRoleDisplayName = (role: UserRole): string => {
		switch (role) {
			case UserRole.SUPER_ADMIN:
				return 'Super Admin';
			case UserRole.PLATFORM_ADMIN:
				return 'Platform Admin';
			case UserRole.CLIENT_USER:
				return 'Client User';
			default:
				return role;
		}
	};

	const getClientTypeDisplayName = (type: string): string => {
		switch (type) {
			case 'ZOHO_BOOKS':
				return 'Zoho Books';
			case 'ODOO':
			case 'ODOO_FINANCE':
				return 'Odoo Finance';
			case 'MSD_FINANCE':
				return 'MSD Finance';
			default:
				return type;
		}
	};

	const getClientTypeBadgeColor = (type: string): string => {
		switch (type) {
			case 'ZOHO_BOOKS':
				return 'bg-purple-100 text-purple-800';
			case 'ODOO':
			case 'ODOO_FINANCE':
				return 'bg-orange-100 text-orange-800';
			case 'MSD_FINANCE':
				return 'bg-indigo-100 text-indigo-800';
			default:
				return 'bg-gray-100 text-gray-800';
		}
	};

	// Get client details from clientId
	const getClientDetails = (clientId?: string): { name: string; type?: string } | null => {
		if (!clientId) return null;
		const client = clients.find((c) => c.id === clientId);
		if (!client) return null;

		// First try to use the type field if available
		let integrationType = client.type;

		// If type is not set, derive it from integrations
		if (!integrationType && client.integrations) {
			if (client.integrations.zoho?.enabled) {
				integrationType = 'ZOHO_BOOKS';
			} else if (client.integrations.odoo?.enabled) {
				integrationType = 'ODOO_FINANCE';
			} else if (client.integrations.msd?.enabled) {
				integrationType = 'MSD_FINANCE';
			}
		}

		return {
			name: client.name,
			...(integrationType ? { type: integrationType } : {}),
		};
	};

	// Calculate user statistics by integration type
	const getUserStats = () => {
		const stats = {
			zoho: { clientCount: 0, userCount: 0, lastSync: null as Date | null },
			odoo: { clientCount: 0, userCount: 0, lastSync: null as Date | null },
			msd: { clientCount: 0, userCount: 0, lastSync: null as Date | null },
			totalClientUsers: 0,
		};

		// Create a map of clientId to integration type
		const clientTypeMap = new Map<string, string>();

		// Count clients by integration type
		clients.forEach((client) => {
			let integrationType = client.type;
			if (!integrationType && client.integrations) {
				if (client.integrations.zoho?.enabled) integrationType = 'ZOHO_BOOKS';
				else if (client.integrations.odoo?.enabled) integrationType = 'ODOO_FINANCE';
				else if (client.integrations.msd?.enabled) integrationType = 'MSD_FINANCE';
			}

			if (integrationType) {
				clientTypeMap.set(client.id, integrationType);

				if (integrationType === 'ZOHO_BOOKS') {
					stats.zoho.clientCount++;
				} else if (integrationType === 'ODOO_FINANCE' || integrationType === 'ODOO') {
					stats.odoo.clientCount++;
				} else if (integrationType === 'MSD_FINANCE') {
					stats.msd.clientCount++;
				}
			}
		});

		// Get last sync times from sync activities
		syncActivities.forEach((activity) => {
			if (activity.clientId) {
				const timestamp = activity.createdAt || activity.timestamp || activity.syncedAt;
				if (timestamp) {
					const integrationType = clientTypeMap.get(activity.clientId);
					const syncDate = new Date(timestamp);

					if (!isNaN(syncDate.getTime())) {
						if (integrationType === 'ZOHO_BOOKS') {
							if (!stats.zoho.lastSync || syncDate > stats.zoho.lastSync) {
								stats.zoho.lastSync = syncDate;
							}
						} else if (integrationType === 'ODOO_FINANCE' || integrationType === 'ODOO') {
							if (!stats.odoo.lastSync || syncDate > stats.odoo.lastSync) {
								stats.odoo.lastSync = syncDate;
							}
						} else if (integrationType === 'MSD_FINANCE') {
							if (!stats.msd.lastSync || syncDate > stats.msd.lastSync) {
								stats.msd.lastSync = syncDate;
							}
						}
					}
				}
			}
		});

		// Count users by integration type
		users.forEach((user) => {
			if (user.role === UserRole.CLIENT_USER) {
				stats.totalClientUsers++;
				const clientDetails = getClientDetails(user.clientId);
				if (clientDetails?.type) {
					if (clientDetails.type === 'ZOHO_BOOKS') {
						stats.zoho.userCount++;
					} else if (clientDetails.type === 'ODOO_FINANCE' || clientDetails.type === 'ODOO') {
						stats.odoo.userCount++;
					} else if (clientDetails.type === 'MSD_FINANCE') {
						stats.msd.userCount++;
					}
				}
			}
		});

		return stats;
	};

	// Format time ago
	const getTimeAgo = (date: Date | null): string => {
		if (!date) return 'Never';
		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffMins = Math.floor(diffMs / 60000);

		if (diffMins < 1) return 'Just now';
		if (diffMins < 60) return `${diffMins} min ago`;

		const diffHours = Math.floor(diffMins / 60);
		if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

		const diffDays = Math.floor(diffHours / 24);
		return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
	};

	const userStats = getUserStats();

	return (
		<div className="space-y-6">
			{/* User Stats - Only visible to ADMIN users */}
			{!isClientUser && (
				<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
					{/* Zoho Books Stats */}
					<div className="bg-white rounded-xl border border-gray-200 p-6">
						<div className="flex items-center justify-between mb-4">
							<h3 className="text-lg font-semibold text-gray-900">Zoho Books</h3>
							<span
								className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getClientTypeBadgeColor('ZOHO_BOOKS')}`}>
								Zoho Books
							</span>
						</div>
						<div className="space-y-3">
							<div className="flex justify-between">
								<span className="text-sm text-gray-600">Clients</span>
								<span className="text-sm font-medium text-gray-900">{userStats.zoho.clientCount}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-sm text-gray-600">Client Users</span>
								<span className="text-sm font-medium text-gray-900">{userStats.zoho.userCount}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-sm text-gray-600">Last Sync</span>
								<span className="text-sm font-medium text-gray-900">{getTimeAgo(userStats.zoho.lastSync)}</span>
							</div>
						</div>
					</div>

					{/* Odoo Stats */}
					<div className="bg-white rounded-xl border border-gray-200 p-6">
						<div className="flex items-center justify-between mb-4">
							<h3 className="text-lg font-semibold text-gray-900">Odoo Finance</h3>
							<span
								className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getClientTypeBadgeColor('ODOO_FINANCE')}`}>
								Odoo Finance
							</span>
						</div>
						<div className="space-y-3">
							<div className="flex justify-between">
								<span className="text-sm text-gray-600">Clients</span>
								<span className="text-sm font-medium text-gray-900">{userStats.odoo.clientCount}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-sm text-gray-600">Client Users</span>
								<span className="text-sm font-medium text-gray-900">{userStats.odoo.userCount}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-sm text-gray-600">Last Sync</span>
								<span className="text-sm font-medium text-gray-900">{getTimeAgo(userStats.odoo.lastSync)}</span>
							</div>
						</div>
					</div>

					{/* MSD Finance Stats */}
					<div className="bg-white rounded-xl border border-gray-200 p-6">
						<div className="flex items-center justify-between mb-4">
							<h3 className="text-lg font-semibold text-gray-900">MSD Finance</h3>
							<span
								className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getClientTypeBadgeColor('MSD_FINANCE')}`}>
								MSD Finance
							</span>
						</div>
						<div className="space-y-3">
							<div className="flex justify-between">
								<span className="text-sm text-gray-600">Clients</span>
								<span className="text-sm font-medium text-gray-900">{userStats.msd.clientCount}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-sm text-gray-600">Client Users</span>
								<span className="text-sm font-medium text-gray-900">{userStats.msd.userCount}</span>
							</div>
							<div className="flex justify-between">
								<span className="text-sm text-gray-600">Last Sync</span>
								<span className="text-sm font-medium text-gray-900">{getTimeAgo(userStats.msd.lastSync)}</span>
							</div>
						</div>
					</div>
				</div>
			)}

			<div className="bg-white rounded-xl border border-gray-200">
				<div className="px-6 py-4 border-b border-gray-200">
					<div className="flex items-center justify-between">
						<h3 className="text-lg font-semibold text-gray-900">User Management</h3>
						<div className="flex items-center space-x-4">
							{/* Search */}
							<div className="relative">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
								<input
									type="text"
									placeholder="Search users..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
								/>
							</div>

							{/* Create User Button */}
							<button
								onClick={() => setShowCreateModal(true)}
								className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center text-sm font-medium">
								<UserPlus className="h-4 w-4 mr-2" />
								Create User
							</button>
						</div>
					</div>
				</div>

				{/* Users Table */}
				<div className="overflow-x-auto">
					{loading ? (
						<div className="flex items-center justify-center py-12">
							<RefreshCw className="animate-spin h-6 w-6 text-blue-600" />
						</div>
					) : filteredUsers.length === 0 ? (
						<div className="text-center py-12">
							<UserPlus className="mx-auto h-12 w-12 text-gray-400" />
							<h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
							<p className="mt-1 text-sm text-gray-500">
								{searchTerm ? 'Try adjusting your search' : 'Get started by creating a new user.'}
							</p>
						</div>
					) : (
						<table className="min-w-full">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										User
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Role
									</th>
									{!isClientUser && (
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Client
										</th>
									)}
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Status
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Last Login
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Created
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-y-gray-200">
								{paginatedUsers.map((user) => (
									<tr key={user.id} className="hover:bg-gray-50">
										<td className="px-6 py-4 whitespace-nowrap">
											<div>
												<div className="text-sm font-medium text-gray-900">{user.fullName}</div>
												<div className="text-sm text-gray-500">{user.email}</div>
											</div>
										</td>
										<td className="px-6 py-4 whitespace-nowrap">
											<span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
												{getRoleDisplayName(user.role)}
											</span>
										</td>
										{!isClientUser && (
											<td className="px-6 py-4">
												{(() => {
													const clientDetails = getClientDetails(user.clientId);
													return clientDetails ? (
														<div className="space-y-1">
															<div className="text-sm font-medium text-gray-900">{clientDetails.name}</div>
															{clientDetails.type && (
																<div>
																	<span
																		className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getClientTypeBadgeColor(clientDetails.type)}`}>
																		{getClientTypeDisplayName(clientDetails.type)}
																	</span>
																</div>
															)}
														</div>
													) : (
														<span className="text-sm text-gray-400">-</span>
													);
												})()}
											</td>
										)}
										<td className="px-6 py-4 whitespace-nowrap">
											<span
												className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
													user.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
												}`}>
												{user.status}
											</span>
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											{user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											{new Date(user.createdAt).toLocaleDateString()}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>

				{/* Pagination Controls */}
				{totalPages > 1 && (
					<div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
						<div className="flex items-center space-x-2">
							<button
								onClick={handlePreviousPage}
								disabled={currentPage === 1 || loading}
								className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-500">
								<ChevronLeft className="h-4 w-4 mr-1" />
								Previous
							</button>
							<button
								onClick={handleNextPage}
								disabled={currentPage === totalPages || loading}
								className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-500">
								Next
								<ChevronRight className="h-4 w-4 ml-1" />
							</button>
						</div>
						<div className="text-sm text-gray-700">
							Page {currentPage} of {totalPages}
						</div>
					</div>
				)}
			</div>

			{/* Create User Modal */}
			{showCreateModal &&
				createPortal(
					<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[55] p-4">
						<div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
							<div className="px-6 py-4 border-b border-gray-200">
								<h3 className="text-lg font-semibold text-gray-900">Create New User</h3>
							</div>

							<form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
								{/* Email */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
									<input
										type="email"
										name="email"
										value={formData.email}
										onChange={handleInputChange}
										className={`w-full px-3 py-2 border ${formErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
										placeholder="user@example.com"
									/>
									{formErrors.email && <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>}
								</div>

								{/* First Name */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
									<input
										type="text"
										name="firstName"
										value={formData.firstName}
										onChange={handleInputChange}
										className={`w-full px-3 py-2 border ${formErrors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
										placeholder="John"
									/>
									{formErrors.firstName && <p className="mt-1 text-sm text-red-600">{formErrors.firstName}</p>}
								</div>

								{/* Last Name */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
									<input
										type="text"
										name="lastName"
										value={formData.lastName}
										onChange={handleInputChange}
										className={`w-full px-3 py-2 border ${formErrors.lastName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
										placeholder="Doe"
									/>
									{formErrors.lastName && <p className="mt-1 text-sm text-red-600">{formErrors.lastName}</p>}
								</div>

								{/* Password */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
									<input
										type="password"
										name="password"
										value={formData.password}
										onChange={handleInputChange}
										className={`w-full px-3 py-2 border ${formErrors.password ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
										placeholder="Min. 8 characters"
									/>
									{formErrors.password && <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>}
								</div>

								{/* Role - Hide dropdown if CLIENT_USER only has one option */}
								{!isClientUser && getAllowedRoles().length > 1 && (
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
										<CustomSelect
											value={formData.role}
											onChange={(value) =>
												setFormData((prev) => ({
													...prev,
													role: value as UserRole,
												}))
											}
											options={getAllowedRoles().map((role) => ({
												value: role,
												label: getRoleDisplayName(role),
											}))}
										/>
									</div>
								)}

								{/* Show role as read-only for CLIENT_USER */}
								{isClientUser && (
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
										<div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700">
											{getRoleDisplayName(UserRole.CLIENT_USER)}
										</div>
									</div>
								)}

								{/* Client Selector - Only for CLIENT_USER role */}
								{formData.role === UserRole.CLIENT_USER && !isClientUser && (
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
										<CustomSelect
											value={formData.clientId || ''}
											onChange={(value) => {
												setFormData((prev) => ({ ...prev, clientId: value }));
												// Clear error for this field
												if (formErrors.clientId) {
													setFormErrors((prev) => {
														const newErrors = { ...prev };
														delete newErrors.clientId;
														return newErrors;
													});
												}
											}}
											options={[
												{ value: '', label: 'Select a client' },
												...clients.map((client) => ({
													value: client.id,
													label: client.name,
												})),
											]}
											className={formErrors.clientId ? 'border-red-500' : ''}
										/>
										{formErrors.clientId && <p className="mt-1 text-sm text-red-600">{formErrors.clientId}</p>}
									</div>
								)}

								{/* Submit Error */}
								{formErrors.submit && (
									<div className="bg-red-50 border border-red-200 rounded-lg p-3">
										<p className="text-sm text-red-600">{formErrors.submit}</p>
									</div>
								)}

								{/* Actions */}
								<div className="flex items-center justify-end space-x-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setShowCreateModal(false)}
										disabled={submitting}
										className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
										Cancel
									</button>
									<button
										type="submit"
										disabled={submitting}
										className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center">
										{submitting ? (
											<>
												<RefreshCw className="animate-spin h-4 w-4 mr-2" />
												Creating...
											</>
										) : (
											<>
												<UserPlus className="h-4 w-4 mr-2" />
												Create User
											</>
										)}
									</button>
								</div>
							</form>
						</div>
					</div>,
					document.body,
				)}
		</div>
	);
};
